import {
  collection, doc, getDocs, getDoc, setDoc, updateDoc, deleteDoc,
  serverTimestamp, Timestamp, query, orderBy, where, getCountFromServer,
} from 'firebase/firestore';
import { db } from '../utils/firebase';
import { DEFAULT_PERMISSIONS } from './orgAuthService';

// SuperAdmin view lives in `clients` (billing, module flags, login URLs).
// Org config (branding, contact, about-us) stays in `organizations`.
// Login accounts for owners/admins/managers live in `staff`.
const CLIENTS = 'clients';
const ORGS    = 'organizations';
const STAFF   = 'staff';

// ── Module catalogue ────────────────────────────────────────────────────────
// Single source of truth for the SuperAdmin portal (create form, configure page,
// client list filters, overview adoption cards).
export const MODULES = [
  { id: 'programmes',     label: 'Programmes Management',  short: 'Programmes',   description: 'Government schemes, org programmes, financial assistance' },
  { id: 'community',      label: 'Community Management',   short: 'Community',    description: 'Volunteer profiles, shift management, hour tracking, member profiles & segmentation' },
  { id: 'supportmgmt',    label: 'Support Management',     short: 'Support Mgmt', description: 'Job board listings, support network groups, mentoring pairs and peer connections' },
  { id: 'events',         label: 'Events Management',      short: 'Events',       description: 'Event setup, registrations, attendance check-in, calendar, content & media' },
  { id: 'communications', label: 'Communications',         short: 'Comms',        description: 'Email campaigns, push notifications, WhatsApp campaigns, campaign listing' },
  { id: 'reports',        label: 'Reports Management',     short: 'Reports',      description: 'Query builder, analytics dashboard, engagement & volunteer reports' },
  { id: 'donors',         label: 'Donor Management',       short: 'Donors',       description: 'Donor profiles, contact CRM, grant & KPI tracking and comms log' },
  { id: 'bespokeai',      label: 'Bespoke AI',             short: 'AI',           description: 'AI-powered community, communications, events, and reports assistance', premium: true },
];

// AI sub-modules — each depends on a parent module being enabled.
export const AI_SUBMODULES = [
  { key: 'communityAI',      label: 'Community AI',      parentKey: 'community',      parentLabel: 'Community Management', description: 'AI-assisted member outreach, at-risk member detection and volunteer matching.' },
  { key: 'communicationsAI', label: 'Communications AI', parentKey: 'communications', parentLabel: 'Communications',       description: 'AI-generated campaign content and personalised copy from member and event data.' },
  { key: 'eventsAI',         label: 'Events AI',         parentKey: 'events',         parentLabel: 'Events Management',    description: 'AI-generated event descriptions, attendance insights and follow-up suggestions.' },
  { key: 'reportsAI',        label: 'Reports AI',        parentKey: 'reports',        parentLabel: 'Reports Management',   description: 'AI-generated insights, trend analysis and natural-language report queries.' },
];

// All off — merge target for stored flags, so a missing key never reads as enabled.
export const DEFAULT_MODULES = {
  programmes: false, community: false, supportmgmt: false, events: false,
  communications: false, reports: false, donors: false, bespokeai: false,
  communityAI: false, communicationsAI: false, eventsAI: false, reportsAI: false,
};

// ── Organisations / clients ─────────────────────────────────────────────────

export const getAllOrganisations = async () => {
  const snap = await getDocs(query(collection(db, CLIENTS), orderBy('createdAt', 'desc')));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getOrganisation = async (orgId) => {
  const snap = await getDoc(doc(db, CLIENTS, orgId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const createOrganisation = async ({ orgName, orgId, status, enabledModules }) => {
  // Guard: setDoc below is an unmerged overwrite. Without this check, reusing an org ID
  // would wipe the existing org's branding, contact, office hours and about-us data.
  const [existingClient, existingOrg] = await Promise.all([
    getDoc(doc(db, CLIENTS, orgId)),
    getDoc(doc(db, ORGS, orgId)),
  ]);
  if (existingClient.exists() || existingOrg.exists()) {
    throw new Error(
      `A client with the ID "${orgId}" already exists. Choose a different ID, or configure the existing client instead of creating a new one.`
    );
  }

  const modules = { ...DEFAULT_MODULES, ...(enabledModules || {}) };
  const plan = Object.entries(modules).filter(([k, v]) => v && !k.endsWith('AI')).map(([k]) => k).join('+') || 'core';

  const clientData = {
    orgId,
    name:          orgName,
    status:        status || 'active',
    email:         '',
    clientDomain:  orgId,
    enabledModules: modules,
    plan,
    adminLoginURL:     `/BespokeBridge/${orgId}/admin/login`,
    volunteerLoginURL: `/BespokeBridge/${orgId}/volunteer/login`,
    memberLoginURL:    `/BespokeBridge/${orgId}/member/login`,
    billing: {
      monthlyPrice: '', monthlyNettPrice: '', discount: '',
      discountStartDate: null, discountEndDate: null, nextBillingDate: null,
    },
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  await setDoc(doc(db, CLIENTS, orgId), clientData);

  await setDoc(doc(db, ORGS, orgId), {
    orgId,
    branding:     { name: orgName, logo: { url: '', width: 200, height: 80 }, colors: {}, typography: {}, images: { hero: '', gallery: [] }, customCSS: '' },
    contact:      { address: '', phone: '', email: '', website: '', socialMedia: {} },
    aboutUs:      { mission: '', vision: '', description: '', yearFounded: '', registrationNumber: '' },
    officeHours:  { schedule: {}, specialClosures: [], notes: '' },
    status:       status || 'active',
    enabledModules: modules,
    createdAt:    serverTimestamp(),
    updatedAt:    serverTimestamp(),
  });

  return { id: orgId, ...clientData };
};

export const updateOrganisationConfig = async (orgId, updates) => {
  await updateDoc(doc(db, CLIENTS, orgId), { ...updates, updatedAt: serverTimestamp() });
  const orgUpdates = {};
  if (updates.enabledModules) orgUpdates.enabledModules = updates.enabledModules;
  if (updates.status)         orgUpdates.status         = updates.status;
  if (Object.keys(orgUpdates).length) {
    await updateDoc(doc(db, ORGS, orgId), { ...orgUpdates, updatedAt: serverTimestamp() });
  }
};

// Full detail for the Configure page: client doc + login accounts
export const getClientById = async (orgId) => {
  const snap = await getDoc(doc(db, CLIENTS, orgId));
  if (!snap.exists()) throw new Error('Client not found in clients collection');
  const data = snap.data();
  const resolvedOrgId = data.orgId || orgId;
  const admins = await getAdminUsersByOrgId(resolvedOrgId);

  // Clients set up before AI sub-modules existed have no sub-flags, and the admin portal treats
  // a missing flag as "on". Show them as on here too, so saving this page doesn't silently switch them off.
  const enabledModules = { ...DEFAULT_MODULES, ...(data.enabledModules || {}) };
  AI_SUBMODULES.forEach(({ key, parentKey }) => {
    if (enabledModules.bespokeai && enabledModules[parentKey] && data.enabledModules?.[key] === undefined) {
      enabledModules[key] = true;
    }
  });

  return {
    id: orgId,
    orgId: resolvedOrgId,
    name: data.name || 'Unnamed Client',
    status: data.status || 'active',
    enabledModules,
    clientDomain: data.clientDomain || '',
    adminLoginURL: data.adminLoginURL || '',
    volunteerLoginURL: data.volunteerLoginURL || '',
    memberLoginURL: data.memberLoginURL || '',
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
    admins,
  };
};

export const updateClientModules = async (orgId, modules) => {
  await updateOrganisationConfig(orgId, { enabledModules: modules });
  return true;
};

// ── Admin (staff) user management — manual Firebase Auth setup ──────────────
// The SuperAdmin creates the login in Firebase Console → Authentication and pastes
// the UID here; this writes the matching `staff` record the admin portal reads at login.

const getAdminUsersByOrgId = async (orgId) => {
  try {
    const snap = await getDocs(query(collection(db, STAFF), where('orgId', '==', orgId)));
    return snap.docs.map(d => {
      const u = d.data();
      return {
        uid: u.uid || d.id,
        name: u.name || 'Unknown',
        email: u.email || 'No email',
        role: u.role || 'admin',
        permissions: u.permissions || {},
      };
    });
  } catch (err) {
    console.error('Error fetching admin users:', err);
    return [];
  }
};

export const addAdminToClient = async (orgId, adminData) => {
  const firebaseUID = (adminData.firebaseUID || '').trim();
  if (!firebaseUID) {
    throw new Error('Firebase UID is required. Please create the user in Firebase Authentication first and provide the UID.');
  }

  const clientSnap = await getDoc(doc(db, CLIENTS, orgId));
  if (!clientSnap.exists()) throw new Error('Could not find client with ID: ' + orgId);
  const resolvedOrgId = clientSnap.data().orgId || orgId;

  const role = adminData.role || 'admin';
  const ref = doc(db, STAFF, firebaseUID);

  const existing = await getDoc(ref);
  if (existing.exists()) {
    const existingOrg = existing.data().orgId || '(unknown organisation)';
    throw new Error(
      `This Firebase UID is already registered to "${existingOrg}". ` +
      'Each admin account must be unique — please create a separate Firebase Authentication account for this client.'
    );
  }

  await setDoc(ref, {
    uid: firebaseUID,
    orgId: resolvedOrgId,
    email: adminData.email,
    name: adminData.name,
    role,
    permissions: DEFAULT_PERMISSIONS[role] || DEFAULT_PERMISSIONS.manager,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  });

  const verify = await getDoc(ref);
  if (!verify.exists()) throw new Error('Document creation failed — record not found after write.');
  return { uid: firebaseUID, email: adminData.email, name: adminData.name, success: true };
};

export const updateAdminUser = async (adminUid, adminData) => {
  await updateDoc(doc(db, STAFF, adminUid), {
    name: adminData.name,
    role: adminData.role,
    permissions: DEFAULT_PERMISSIONS[adminData.role] || DEFAULT_PERMISSIONS.manager,
    updatedAt: Timestamp.now(),
  });
  return true;
};

export const deleteAdminUser = async (adminUid) => {
  await deleteDoc(doc(db, STAFF, adminUid));
  return true;
};

// ── Counts ──────────────────────────────────────────────────────────────────

export const getMemberCount = async (orgId) => {
  try {
    const snap = await getCountFromServer(
      query(collection(db, 'members'), where('orgId', '==', orgId))
    );
    return snap.data().count;
  } catch { return '—'; }
};

export const getVolunteerCount = async (orgId) => {
  try {
    const snap = await getCountFromServer(
      query(collection(db, 'volunteers'), where('orgId', '==', orgId))
    );
    return snap.data().count;
  } catch { return '—'; }
};

export const getOpenTicketCount = async () => {
  try {
    const snap = await getCountFromServer(
      query(collection(db, 'tickets'), where('ticketStatus', '!=', 'Closed'))
    );
    return snap.data().count;
  } catch { return 0; }
};

// ── Bespoke staff (business cards) ──────────────────────────────────────────

export const getAllBespokeStaff = async () => {
  const snap = await getDocs(query(collection(db, 'bespokeStaff'), orderBy('createdAt', 'desc')));
  return snap.docs.map(d => {
    const data = d.data();
    return {
      id: d.id,
      name: data.name || '',
      title: data.title || '',
      email: data.email || '',
      phone: data.phone || '',
      address: data.address || '',
      logoUrl: data.logoUrl || '',
      logoUrlBack: data.logoUrlBack || '',
      qrCodeUrl: data.qrCodeUrl || '',
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
    };
  });
};
